import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { useIsMobile } from "@/hooks/useIsMobile";

// Aurora-style background — a louder, moving sibling of AmbientGlow, meant
// for the handful of moments that need to feel like a disruptive "hero
// beat" inside the page rather than a quiet section. Three large, blurred
// blobs drift on their own slow loop AND parallax-shift with scroll
// (via useScroll on this component's own wrapper), so the block keeps
// moving as the reader passes through it, not just on a timer.
//
// Perf note: each blob is split into two nested elements so the two kinds
// of motion never fight over the same `transform`. The OUTER element
// carries the scroll-linked parallax (y) — framer-motion, because it has
// to track actual scroll position, and it's just a cheap transform, not a
// filter, so it stays on for every device. The INNER element carries the
// x-drift as a plain CSS `@keyframes` animation (.animate-glow-drift in
// index.css) instead of framer-motion's per-frame JS interpolation — it
// runs on the compositor thread, gated the same way as before (only while
// in/near the viewport, off on mobile, where the blur radius also drops
// via the `lg:` breakpoint). Three simultaneous JS-driven blur filters
// animating regardless of scroll position, combined with AmbientGlow
// elsewhere on the page, was heavy enough to starve the main thread — the
// hero video couldn't even finish loading while off-screen instances kept
// animating.
//
// tone="brand"       — terracota / accentSoft, full-strength blobs. Dark
//                       (ink) panels — the reinforcement/hero-style block.
// tone="brand-light"  — same brand palette, multiply-blended and dimmer.
//                       Paper/light panels — "¿Cómo trabajamos?".
// tone="signal"        — cobalt / lime / orange (default), dark panels.
const TONES = {
  signal: {
    blend: "",
    blobs: [
      { color: "#5A64FF", opacity: 25 },
      { color: "#B7F33D", opacity: 20 },
      { color: "#FF6433", opacity: 18 },
    ],
  },
  brand: {
    blend: "",
    blobs: [
      { color: "#B9513B", opacity: 30 },
      { color: "#D9A17A", opacity: 22 },
      { color: "#B9513B", opacity: 16 },
    ],
  },
  "brand-light": {
    blend: "mix-blend-multiply",
    blobs: [
      { color: "#B9513B", opacity: 14 },
      { color: "#D9A17A", opacity: 22 },
      { color: "#B9513B", opacity: 10 },
    ],
  },
};

export function AuroraGlow({ className = "", tone = "signal" }) {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const inView = useInView(ref, { margin: "200px 0px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const parallaxYRaw = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const parallaxYReverseRaw = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const { blend, blobs } = TONES[tone] || TONES.signal;
  const shouldDrift = inView && !isMobile;
  // Scroll-linked parallax off on mobile/tablet — static position instead
  // of a per-scroll-frame transform write.
  const parallaxY = isMobile ? 0 : parallaxYRaw;
  const parallaxYReverse = isMobile ? 0 : parallaxYReverseRaw;

  return (
    <div ref={ref} className={`pointer-events-none absolute inset-0 overflow-hidden ${blend} ${className}`} aria-hidden="true">
      <motion.div
        className="absolute w-[50vw] h-[50vw] max-w-[560px] max-h-[560px]"
        style={{ top: "-20%", left: "-10%", y: parallaxY }}
      >
        <div
          className={`w-full h-full rounded-full blur-2xl lg:blur-[130px] ${shouldDrift ? "animate-glow-drift" : ""}`}
          style={{
            backgroundColor: blobs[0].color,
            opacity: blobs[0].opacity / 100,
            "--drift-x": "40px",
            "--drift-duration": "20s",
          }}
        />
      </motion.div>
      <motion.div
        className="absolute w-[40vw] h-[40vw] max-w-[460px] max-h-[460px]"
        style={{ bottom: "-15%", right: "-8%", y: parallaxYReverse }}
      >
        <div
          className={`w-full h-full rounded-full blur-2xl lg:blur-[130px] ${shouldDrift ? "animate-glow-drift" : ""}`}
          style={{
            backgroundColor: blobs[1].color,
            opacity: blobs[1].opacity / 100,
            "--drift-x": "-32px",
            "--drift-duration": "24s",
            "--drift-delay": "1.5s",
          }}
        />
      </motion.div>
      <motion.div
        className="absolute w-[34vw] h-[34vw] max-w-[380px] max-h-[380px]"
        style={{ top: "35%", left: "38%", y: parallaxY }}
      >
        <div
          className={`w-full h-full rounded-full blur-2xl lg:blur-[120px] ${shouldDrift ? "animate-glow-drift" : ""}`}
          style={{
            backgroundColor: blobs[2].color,
            opacity: blobs[2].opacity / 100,
            "--drift-x": "22px",
            "--drift-duration": "26s",
            "--drift-delay": "3s",
          }}
        />
      </motion.div>
    </div>
  );
}
